import { formatExpiration } from 'utils/functions';
import type { MindbodyContract } from 'types/contract';
import type { Service } from 'types/service';
import type {
  PricingCategory,
  PricingContract,
  PricingGlobalTexts,
  PricingImage,
  PricingRibbon,
  PricingService,
  PricingTopBlock,
} from 'types/pricing';

/** Sentinel value stored in override fields to explicitly hide the text. */
const HIDDEN = '__hidden__';

/**
 * Resolved view of a pricing service after merging the admin config
 * (PricingService) with the live Mindbody data (Service). Everything that
 * the PackCard needs to render comes from this object — no further lookups
 * required at the component level.
 */
export interface MergedPricingService {
  /** Stable id from the admin config (used as React key, drag handles, etc.). */
  id: string;
  /**
   * The full live Mindbody Service used by the checkout navigation hook.
   * `null` for custom cards that are not linked to any Mindbody service.
   */
  service: Service | null;
  /** Display title — admin override or live Mindbody name. */
  title: string;
  /** Hex price label, e.g. "120€". */
  priceLabel: string;
  /**
   * Per-session price label when count > 1 (e.g. "12€/séance"), null otherwise.
   * Computed with the same rounding rule as Services.tsx (`round(price/count * 2) / 2`).
   */
  pricePerSessionLabel: string | null;
  /** Per-session price alone (e.g. "12€"), to render bold. Null when count <= 1. */
  pricePerSessionPrice: string | null;
  /** Suffix from the template after the {price} placeholder (e.g. " / séance"). */
  pricePerSessionSuffix: string;
  /** Optional sub-label, e.g. "soit 120€ le pack", null when count <= 1. */
  priceSubLabel: string | null;
  /** Resolved validity sentence, e.g. "Valable 1 mois*". Empty string when nothing is set. */
  validity: string;
  /** Resolved limit text. Empty when nothing to display. */
  limitText: string;
  /** Whether to show the "Payez en plusieurs fois avec Alma" sentence. */
  almaEnabled: boolean;
  /** Resolved CTA label, e.g. "Acheter". */
  ctaLabel: string;
  /** Optional decorative image (admin-uploaded). */
  image: PricingImage | null;
  /** Optional ribbon overlay. */
  ribbon: PricingRibbon | null;
  /** Optional background color (hex). When set, card renders with colored bg + white text. */
  bgColor?: string;
  /** Title text color override (hex). */
  titleColor?: string;
  /** Price text color override (hex). */
  priceColor?: string;
  /** General text color override (hex). */
  textColor?: string;
  /** CTA button background color override (hex). */
  ctaColor?: string;
  /** Accent color for ribbon + CTA (service-level). */
  accentColor?: string;
  /** When true, the price block is hidden. */
  hidePrice?: boolean;
}

/**
 * Render `Valable {length} {unit}*` style template against a live Mindbody
 * service. Falls back to the existing `formatExpiration` helper when the
 * template is empty so the behavior matches the legacy Services.tsx for
 * services that don't have a custom template defined.
 */
function renderValidityTemplate(template: string, live: Service): string {
  const length = live.ExpirationLength ?? 0;
  const unit = live.ExpirationUnit ?? '';
  if (!template) {
    return formatExpiration(length, unit) || '';
  }
  if (!length || !unit) return '';
  // Convert "Months" → "mois", "Days" → "jours" using the same logic as
  // formatExpiration but inline so we can interpolate into the template.
  const unitMap: Record<string, { singular: string; plural: string }> = {
    day: { singular: 'jour', plural: 'jours' },
    days: { singular: 'jour', plural: 'jours' },
    month: { singular: 'mois', plural: 'mois' },
    months: { singular: 'mois', plural: 'mois' },
  };
  const entry = unitMap[unit.toLowerCase()];
  const unitText = entry ? (length > 1 ? entry.plural : entry.singular) : unit;
  return template
    .replace(/\{length\}/g, String(length))
    .replace(/\{unit\}/g, unitText);
}

/**
 * Truncate to one decimal place, always rounding DOWN (e.g. 18.98 → 18.9,
 * 19.97 → 19.9). On tronque plutôt que d'arrondir pour obtenir un prix à la
 * séance toujours inférieur au réel calcul — psychologie de prix classique
 * (19,9€ lit mieux que 20€).
 */
function roundToOneDecimal(n: number): number {
  return Math.floor(n * 10) / 10;
}

/**
 * Format a price as "120€" (no decimals when integer) or "12,5€" (with comma
 * when fractional). Matches the visual format used by the legacy page.
 */
function formatPrice(value: number): string {
  if (Number.isInteger(value)) return `${value}€`;
  return `${String(value).replace('.', ',')}€`;
}

/**
 * Render the global "per-session price" template against a formatted price.
 * Returns null when the template is empty or explicitly hidden so the caller
 * skips rendering the label entirely.
 */
function renderPricePerSessionTemplate(template: string, pricePerSession: number): string | null {
  if (!template || template === HIDDEN) return null;
  return template.replace(/\{price\}/g, formatPrice(pricePerSession));
}

/**
 * Return the suffix portion of the per-session template (everything after the
 * `{price}` placeholder), so the price itself can be rendered bold while the
 * suffix stays light. Empty string when the template is empty/hidden or has no
 * placeholder.
 */
function pricePerSessionSuffixFor(template: string): string {
  if (!template || template === HIDDEN) return '';
  const idx = template.indexOf('{price}');
  if (idx < 0) return '';
  return template.slice(idx + '{price}'.length);
}

/**
 * Render the admin-configurable `priceSubLabel` template. The default is
 * `"soit {price}€ les {count} séances"` — note the `€` lives inside the
 * template, so `{price}` is substituted with the raw numeric price (no
 * currency symbol) to match that convention.
 */
function renderPriceSubLabelTemplate(template: string, price: number, count: number): string | null {
  if (!template || template === HIDDEN) return null;
  const priceText = Number.isInteger(price) ? String(price) : String(price).replace('.', ',');
  return template
    .replace(/\{price\}/g, priceText)
    .replace(/\{count\}/g, String(count));
}

/**
 * Render the global "contract engagement" template against the live
 * autopay count. Returns empty string when the template is hidden or empty.
 */
function renderContractEngagementTemplate(template: string, count: number): string {
  if (!template || template === HIDDEN) return '';
  return template.replace(/\{count\}/g, String(count));
}

/**
 * Resolve a 3-level cascade for a string field: service override → category
 * default → global default. Empty strings are treated as "not set" so the
 * cascade walks through them, which is exactly what we want for repetitive
 * texts like the validity template that the admin should be able to set
 * once at the global level.
 *
 * The special sentinel `__hidden__` means "explicitly empty" — the field
 * should display nothing, bypassing the cascade entirely.
 */
function cascade(serviceOverride: string | null, categoryValue: string, globalValue: string): string {
  if (serviceOverride === HIDDEN) return '';
  if (serviceOverride !== null && serviceOverride !== '') return serviceOverride;
  if (categoryValue === HIDDEN) return '';
  if (categoryValue !== '') return categoryValue;
  if (globalValue === HIDDEN) return '';
  return globalValue;
}

/**
 * Merge an admin-config PricingService with its live Mindbody counterpart.
 * `live` may be undefined when the service was deleted/discontinued in
 * Mindbody — in that case the function returns `null` so the caller can
 * filter it out (with a console warning) instead of rendering a broken card.
 *
 * The cascade chain for repetitive texts is:
 *   service override → category default → global default
 * so the admin can set "Valable {length} {unit}*", "Un seul achat par
 * client…" and "Acheter" ONCE in the General tab and they automatically
 * apply to every category, with per-category and per-service overrides
 * available when truly needed.
 */
export function mergePricingService(
  cfg: PricingService,
  category: PricingCategory,
  globalTexts: PricingGlobalTexts,
  live: Service | undefined,
): MergedPricingService | null {
  const isCustomCard = !cfg.mindbodyId;

  // Custom cards don't need a live Mindbody service.
  if (!isCustomCard && !live) {
    console.warn(
      `[ServicesContracts] Service Mindbody introuvable pour mindbodyId=${cfg.mindbodyId} ` +
        `(catégorie "${category.label}"). Service masqué côté front.`,
    );
    return null;
  }

  // Title: __hidden__ → empty, null → live name, string → override
  const title = cfg.titleOverride === HIDDEN
    ? ''
    : (cfg.titleOverride ?? live?.Name ?? (isCustomCard ? '' : `Service ${cfg.mindbodyId}`));

  // Price & count — only for Mindbody-linked services.
  const count = live && live.Count > 0 ? live.Count : 1;
  const priceLabel = live ? formatPrice(live.Price) : '';
  const pricePerSession = live && count > 1 ? roundToOneDecimal(live.Price / count) : null;
  const pricePerSessionTemplate = globalTexts.pricePerSessionTemplate ?? '';
  const pricePerSessionLabel =
    pricePerSession !== null
      ? renderPricePerSessionTemplate(pricePerSessionTemplate, pricePerSession)
      : null;
  const pricePerSessionPrice = pricePerSession !== null ? formatPrice(pricePerSession) : null;
  const pricePerSessionSuffix = pricePerSession !== null ? pricePerSessionSuffixFor(pricePerSessionTemplate) : '';
  const priceSubLabel = live && count > 1
    ? renderPriceSubLabelTemplate(globalTexts.priceSubLabel ?? '', live.Price, count)
    : null;

  // Resolve the validity template through the cascade, then interpolate
  // {length}/{unit} against the live service. When any level of the cascade
  // is __hidden__, validity is forced to '' (no fallback to formatExpiration).
  const validityExplicitlyHidden =
    cfg.validityOverride === HIDDEN ||
    (!cfg.validityOverride && category.defaults.validityTemplate === HIDDEN) ||
    (!cfg.validityOverride && !category.defaults.validityTemplate && globalTexts.validityTemplate === HIDDEN);
  const resolvedTemplate = validityExplicitlyHidden ? '' : cascade(cfg.validityOverride, category.defaults.validityTemplate, globalTexts.validityTemplate);
  const validity = validityExplicitlyHidden
    ? ''
    : resolvedTemplate && live
      ? renderValidityTemplate(resolvedTemplate, live)
      : (resolvedTemplate || (live ? formatExpiration(live.ExpirationLength ?? 0, live.ExpirationUnit ?? '') || '' : ''));

  const limitText = cascade(cfg.limitTextOverride, category.defaults.limitText, globalTexts.limitText);
  const ctaLabel = cascade(cfg.ctaLabelOverride, category.defaults.ctaLabel, globalTexts.ctaLabel) || 'Acheter';
  const almaEnabled = cfg.almaOverride === false ? false : (cfg.almaOverride ?? category.defaults.almaEnabled);

  return {
    id: cfg.id,
    service: live ?? null,
    title,
    priceLabel,
    pricePerSessionLabel,
    pricePerSessionPrice,
    pricePerSessionSuffix,
    priceSubLabel,
    validity,
    limitText,
    almaEnabled,
    ctaLabel,
    image: cfg.image,
    ribbon: cfg.ribbon,
    accentColor: cfg.accentColor,
    bgColor: cfg.bgColor,
    textColor: cfg.textColor,
    hidePrice: cfg.hidePrice,
  };
}

/**
 * Variant of `mergePricingService` for top blocks. Top blocks store their
 * text values directly (not as overrides) so the cascade is just 2-level:
 * top block value → global default. Returns null when the linked Mindbody
 * service was deleted/discontinued so the caller can hide the card.
 */
export function mergeTopBlock(
  block: PricingTopBlock,
  globalTexts: PricingGlobalTexts,
  live: Service | undefined,
): MergedPricingService | null {
  const isCustomCard = !block.mindbodyId;

  // Custom cards don't need a live Mindbody service.
  if (!isCustomCard && !live) {
    console.warn(
      `[ServicesContracts] Service Mindbody introuvable pour top block mindbodyId=${block.mindbodyId}. Bloc masqué.`,
    );
    return null;
  }

  // Handle __hidden__ sentinel: explicitly hide the field.
  const title = block.titleOverride === HIDDEN
    ? ''
    : (block.titleOverride ?? live?.Name ?? (isCustomCard ? '' : `Service ${block.mindbodyId}`));

  // Price & count — only for Mindbody-linked blocks.
  const count = live && live.Count > 0 ? live.Count : 1;
  const priceLabel = live ? formatPrice(live.Price) : '';
  const pricePerSession = live && count > 1 ? roundToOneDecimal(live.Price / count) : null;
  const pricePerSessionTemplate = globalTexts.pricePerSessionTemplate ?? '';
  const pricePerSessionLabel =
    pricePerSession !== null
      ? renderPricePerSessionTemplate(pricePerSessionTemplate, pricePerSession)
      : null;
  const pricePerSessionPrice = pricePerSession !== null ? formatPrice(pricePerSession) : null;
  const pricePerSessionSuffix = pricePerSession !== null ? pricePerSessionSuffixFor(pricePerSessionTemplate) : '';
  const priceSubLabel = live && count > 1
    ? renderPriceSubLabelTemplate(globalTexts.priceSubLabel ?? '', live.Price, count)
    : null;

  // Top blocks: 2-level cascade (block value → global). Empty string means
  // "use the global default". __hidden__ means "explicitly empty".
  const validity = block.validity === HIDDEN
    ? ''
    : (() => {
        const resolvedTemplate = block.validity || globalTexts.validityTemplate;
        return resolvedTemplate && live
          ? renderValidityTemplate(resolvedTemplate, live)
          : (resolvedTemplate || (live ? formatExpiration(live.ExpirationLength ?? 0, live.ExpirationUnit ?? '') || '' : ''));
      })();
  const limitText = block.limitText === HIDDEN ? '' : (block.limitText || globalTexts.limitText);
  const ctaLabel = block.ctaLabel === HIDDEN ? '' : (block.ctaLabel || globalTexts.ctaLabel || 'Acheter');

  return {
    id: block.id,
    service: live ?? null,
    title,
    priceLabel,
    pricePerSessionLabel,
    pricePerSessionPrice,
    pricePerSessionSuffix,
    priceSubLabel,
    validity,
    limitText,
    almaEnabled: block.almaEnabled,
    ctaLabel,
    image: block.image,
    ribbon: block.ribbon,
    bgColor: block.bgColor,
    titleColor: block.titleColor,
    priceColor: block.priceColor,
    textColor: block.textColor,
    ctaColor: block.ctaColor,
  };
}

/**
 * Resolved view of a contract after merging admin config + live Mindbody
 * data. The shape is intentionally close to MergedPricingService so the
 * ContractCard component can stay simple, but with contract-specific extras
 * (recurringPriceLabel, scheduleLabel) for the recurring payment display.
 */
export interface MergedPricingContract {
  /** Stable id from the admin config (used as React key + drag handle). */
  id: string;
  /** The live Mindbody contract — exposed so the checkout flow can use it. */
  contract: MindbodyContract;
  title: string;
  /** Recurring payment label, e.g. "59€ / mois". */
  priceLabel: string;
  /** Price portion, e.g. "59€". Bold-eligible in the UI. */
  priceAmount: string;
  /** Suffix portion, e.g. " / mois". Rendered in regular weight. */
  priceSuffix: string;
  /** Sub-text describing the recurring schedule, e.g. "12 prélèvements mensuels". */
  scheduleLabel: string | null;
  /**
   * Per-session price label ("soit 22,3€ par séance"), computed from the
   * recurring amount / sessionsPerPeriod. Null when the admin didn't set
   * sessionsPerPeriod on the contract.
   */
  pricePerSessionLabel: string | null;
  /** Resolved validity sentence, e.g. "Engagement 12 mois". */
  validity: string;
  limitText: string;
  ctaLabel: string;
  image: PricingImage | null;
  ribbon: PricingRibbon | null;
  hidePrice?: boolean;
  /** URL du PDF des CGV du contrat (saisie en admin). */
  contractPdfUrl?: string | null;
  /**
   * HTML des CGV administrable dans l'onglet Général. Vide/null = le
   * `ContractCard` retombe sur son bloc CGV hardcodé.
   */
  contractTerms?: string | null;
}

/**
 * Translate a Mindbody `FrequencyTimeUnit` value into a human French
 * sentence used in card labels.
 */
function frequencyUnitToLabel(unit: string, plural: boolean): string {
  switch (unit) {
    case 'Daily':
      return plural ? 'jours' : 'jour';
    case 'Weekly':
      return plural ? 'semaines' : 'semaine';
    case 'Monthly':
      return plural ? 'mois' : 'mois';
    case 'Yearly':
      return plural ? 'ans' : 'an';
    default:
      return unit.toLowerCase();
  }
}

/**
 * Per-payment-frequency suffix used to format the recurring price label,
 * e.g. "59€ / mois", "59€ / semaine", "79€ / 4 semaines".
 *
 * `value` is `AutopaySchedule.FrequencyValue` : a contract billed every
 * 4 weeks is `Weekly` × 4 in Mindbody, so the unit alone is not enough
 * (same rule as `recurringLabel` in the checkout recap).
 */
function frequencyToShortLabel(unit: string, value: number): string {
  const n = Number.isFinite(value) && value > 1 ? Math.floor(value) : 1;
  switch (unit) {
    case 'Daily':
      return n > 1 ? ` / ${n} jours` : ' / jour';
    case 'Weekly':
      return n > 1 ? ` / ${n} semaines` : ' / semaine';
    case 'Monthly':
      return n > 1 ? ` / ${n} mois` : ' / mois';
    case 'Yearly':
      return n > 1 ? ` / ${n} ans` : ' / an';
    default:
      return '';
  }
}

/**
 * Merge an admin-config PricingContract with its live Mindbody counterpart.
 * Returns null when the linked contract was deleted/discontinued in
 * Mindbody, so the caller can hide the card with a console warning.
 */
export function mergePricingContract(
  cfg: PricingContract,
  globalTexts: PricingGlobalTexts,
  live: MindbodyContract | undefined,
  /**
   * Services by id — used to resolve `ContractItem.Id` → `Service.Count` so we
   * can auto-detect the real number of sessions per period (a ContractItem
   * typically carries Quantity=1 pointing to a "4 séances" service whose
   * Count=4).
   */
  servicesById?: Map<string, Service>,
): MergedPricingContract | null {
  if (!live) {
    console.warn(
      `[ServicesContracts] Contrat Mindbody introuvable pour mindbodyContractId=${cfg.mindbodyContractId}. Carte masquée.`,
    );
    return null;
  }

  const title = cfg.titleOverride ?? live.Name ?? `Contract ${cfg.mindbodyContractId}`;

  // Recurring price: prefer the recurring amount, fall back to first
  // payment, fall back to the total. The Mindbody API returns 0 for many
  // fields when the contract is discounted/promotional, so we try several.
  const recurring = live.RecurringPaymentAmountTotal || live.FirstPaymentAmountTotal || live.TotalContractAmountTotal || 0;
  const freqUnit = live.AutopaySchedule?.FrequencyTimeUnit ?? '';
  const freqValue = live.AutopaySchedule?.FrequencyValue ?? 1;
  const freqSuffix = frequencyToShortLabel(freqUnit, freqValue);
  const priceAmount = formatPrice(recurring);
  const priceSuffix = freqSuffix;
  const priceLabel = `${priceAmount}${priceSuffix}`;

  // Schedule label: e.g. "12 prélèvements mensuels" or "Renouvellement automatique"
  let scheduleLabel: string | null = null;
  const numAutopays = live.NumberOfAutopays;
  if (numAutopays && numAutopays > 0 && freqUnit) {
    const unitLabel = frequencyUnitToLabel(freqUnit, numAutopays > 1);
    scheduleLabel = `${numAutopays} ${unitLabel}`;
  } else if (live.ActionUponCompletionOfAutopays === 'ContractAutomaticallyRenews') {
    scheduleLabel = 'Renouvellement automatique';
  }

  // Validity: contracts default to the `contractEngagementTemplate` global
  // (e.g. "Engagement {count} mois") when the admin leaves `validityOverride`
  // blank. `__hidden__` forces an empty string. An explicit override string
  // takes precedence over the template.
  let validity = cfg.validityOverride === HIDDEN ? '' : (cfg.validityOverride ?? '');
  if (!validity && numAutopays && freqUnit === 'Monthly') {
    validity = renderContractEngagementTemplate(globalTexts.contractEngagementTemplate ?? '', numAutopays);
  }

  const rawLimitText = cfg.limitTextOverride ?? globalTexts.limitText ?? '';
  const limitText = rawLimitText === HIDDEN ? '' : rawLimitText;
  // Default CTA for contracts is "Souscrire", not "Acheter".
  const rawCtaLabel = cfg.ctaLabelOverride ?? 'Souscrire';
  const ctaLabel = rawCtaLabel === HIDDEN ? '' : rawCtaLabel;

  // Price-per-session label — auto-détection depuis ContractItems. Chaque
  // item référence un service (Id) dont le `Count` donne le vrai nombre de
  // séances ; item.Quantity est le multiplicateur (généralement 1). On
  // croise donc items × services pour obtenir le total par période. Fallback
  // sur la surcharge admin si renseignée. Format piloté par le template
  // global `pricePerSessionTemplate`, identique aux packs.
  const autoSessionsPerPeriod = (live.ContractItems ?? [])
    .filter((item) => !item.OneTimeItem && typeof item.Quantity === 'number' && item.Quantity > 0)
    .reduce((sum, item) => {
      const linkedService = servicesById?.get(String(item.Id));
      const sessionsPerInstance = linkedService && linkedService.Count > 0 ? linkedService.Count : 1;
      return sum + item.Quantity * sessionsPerInstance;
    }, 0);
  const sessionsPerPeriod =
    cfg.sessionsPerPeriod && cfg.sessionsPerPeriod > 0
      ? cfg.sessionsPerPeriod
      : autoSessionsPerPeriod > 0
        ? autoSessionsPerPeriod
        : null;
  // `hidePricePerSession` court-circuite tout : un abonnement illimité n'a pas
  // de prix à la séance, et l'auto-détection retomberait sur 1 séance (donc
  // sur le prix mensuel entier) faute de ContractItem décomptable.
  const pricePerSessionLabel =
    !cfg.hidePricePerSession && sessionsPerPeriod && recurring > 0
      ? renderPricePerSessionTemplate(globalTexts.pricePerSessionTemplate ?? '', roundToOneDecimal(recurring / sessionsPerPeriod))
      : null;

  return {
    id: cfg.id,
    contract: live,
    title,
    priceLabel,
    priceAmount,
    priceSuffix,
    scheduleLabel,
    pricePerSessionLabel,
    validity,
    limitText,
    ctaLabel,
    image: cfg.image,
    ribbon: cfg.ribbon,
    hidePrice: cfg.hidePrice,
    contractPdfUrl: cfg.contractPdfUrl || globalTexts.defaultContractPdfUrl || null,
    contractTerms: cfg.contractTermsOverride || globalTexts.defaultContractTerms || null,
  };
}
